import React from 'react';
import { BarChart, Trash2, Layers } from 'lucide-react';
import { formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сравнения сохраненных оценок
 */
const ComparisonSidebar = ({ 
  savedAssessments, 
  deleteSavedAssessment, 
  loadSavedAssessment 
}) => {
  // Если нет сохраненных оценок
  if (!savedAssessments || savedAssessments.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-orange-500" />
          Сравнение оценок
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные результаты оценок для сравнения.
        </p>
      </div>
    );
  }

  // Получение названия модели расчета
  const getModelName = (modelId) => {
    const models = {
      'altman-z': 'Модель Альтмана',
      'fulmer': 'Модель Фулмера',
      'springate': 'Модель Спрингейта',
      'taffler': 'Модель Таффлера',
      'beaver': 'Модель Бивера',
      'saifullin-kadykov': 'Модель Сайфуллина-Кадыкова',
      'comprehensive': 'Комплексная оценка'
    };
    return models[modelId] || modelId;
  };

  // Форматирование даты
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  // Получение цвета для индикатора риска
  const getRiskColor = (riskLevel) => {
    riskLevel = riskLevel.toLowerCase();
    if (riskLevel.includes('низкий') || riskLevel.includes('очень низкий')) {
      return 'bg-green-500';
    } else if (riskLevel.includes('умеренный')) {
      return 'bg-yellow-500';
    } else if (riskLevel.includes('высокий')) {
      return 'bg-orange-500';
    } else if (riskLevel.includes('очень высокий') || riskLevel.includes('критический')) {
      return 'bg-red-500';
    }
    return 'bg-gray-500';
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-orange-500" />
          Сравнение оценок
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedAssessments.length} {savedAssessments.length === 1 ? 'оценка' : savedAssessments.length < 5 ? 'оценки' : 'оценок'}
        </span>
      </div>

      <div className="space-y-4">
        {savedAssessments.map((assessment) => (
          <div 
            key={assessment.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {assessment.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Layers className="w-4 h-4" />}
                  onClick={() => loadSavedAssessment(assessment.id)}
                  title="Загрузить параметры"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedAssessment(assessment.id)}
                  title="Удалить"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex justify-between mb-1">
                <span>Дата оценки:</span>
                <span>
                  {formatDate(assessment.date)}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Модель:</span>
                <span>
                  {getModelName(assessment.calculationModel)}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Активы:</span>
                <span className="font-medium">
                  {formatNumber(assessment.results.financialData.totalAssets, 0)} ₽
                </span>
              </div>
              <div className="flex justify-between mb-2">
                <span>Обязательства:</span>
                <span>
                  {formatNumber(assessment.results.financialData.totalLiabilities, 0)} ₽
                </span>
              </div>
              
              {/* Индикатор риска */}
              <div className="flex justify-between items-center">
                <span>Риск банкротства:</span>
                <div className="flex items-center gap-2">
                  <div className={`h-3 w-3 rounded-full ${getRiskColor(assessment.results.riskLevel)}`}></div>
                  <span className="font-semibold text-gray-900 dark:text-gray-100">
                    {assessment.results.riskLevel}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ComparisonSidebar;